import { Button } from "@/components/ui/baseUi/button"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import {
  errorToastClassName,
  sonnerToast,
  successToastClassName,
  appToast as toast,
} from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { ArrowDrawIcon } from "@images/svgIcons/arrowDrawIcon"
import { DurationType } from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { getFormattedCouponReduction } from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency, getCurrencySymbol } from "@modules/currency/currency"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { useSubscriberDiscountFormik } from "@modules/programs/programSubscriber/formik/useSubscriberDiscountFormik"
import { useProgramSubscriberActions } from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { ChevronLeft, LoaderCircle } from "lucide-react"
import { useMemo, useState } from "react"

type ProgramSubscriberDiscountFormProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  moveToSubscriberDetails: () => void
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberDiscountForm({
  subscriberDetails,
  moveToSubscriberDetails,
  programId,
  className,
  ...props
}: ProgramSubscriberDiscountFormProps) {
  const language = useLanguage()

  const [isResumeVisible, setIsResumeVisible] = useState(false)
  const { formik, isSubmitting } = useSubscriberDiscountFormik(programId, subscriberDetails, moveToSubscriberDetails)
  const { removeSubscriptionDiscount, isRemovingSubscriptionDiscount } = useProgramSubscriberActions(
    programId,
    subscriberDetails,
  )

  const discountTypesOptions = [
    { value: CouponType.PERCENT, label: word("program.subscribers.subscriptionDetails.discount.percentInputLabel") },
    { value: CouponType.DIRECT, label: word("program.subscribers.subscriptionDetails.discount.directInputLabel") },
  ]

  const durationTypesOptions = [
    {
      value: DurationType.MONTHLY,
      label: word("program.subscribers.subscriptionDetails.discount.monthlyInputLabel"),
    },
    {
      value: DurationType.UNLIMITED,
      label: word("program.subscribers.subscriptionDetails.discount.unlimitedInputLabel"),
    },
  ]

  const handleOnDelete = () => {
    if (subscriberDetails.coupon) {
      void sonnerToast.promise(
        removeSubscriptionDiscount()
          .then(() => {
            setIsResumeVisible(false)
            formik.resetForm()
            toast.success(word("program.subscribers.subscriptionDetails.discount.toastDeleteSuccess"), {
              classNames: successToastClassName(),
            })
            moveToSubscriberDetails()
          })
          .catch((e: any) => {
            console.error(LoggerType.Service + "remove discount", e)
            toast.error(word("program.subscribers.subscriptionDetails.discount.toastDeleteError"), {
              classNames: errorToastClassName(),
            })
          }),
      )
    }
  }

  const priceDiscount = useMemo(() => {
    if (formik.values.value > 0 && subscriberDetails.paidPrice) {
      let priceAfterDiscount = subscriberDetails.paidPrice.value
      if (formik.values.type === CouponType.PERCENT) {
        const discountPercentage = formik.values.value / 100
        priceAfterDiscount = priceAfterDiscount - priceAfterDiscount * discountPercentage
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      } else if (formik.values.type === CouponType.DIRECT) {
        priceAfterDiscount = priceAfterDiscount - formik.values.value
      }
      return priceAfterDiscount
    } else return 0
  }, [formik.values.value, formik.values.type, subscriberDetails.paidPrice])

  return (
    <div className={cn("flex grow flex-col gap-5 p-2 pt-1", className)} {...props}>
      {/* Form header */}
      <div className="flex flex-col items-center gap-3">
        <div className="relative flex w-full flex-row justify-center">
          <Button variant="underline" onClick={moveToSubscriberDetails} className="absolute left-0">
            <ChevronLeft color="currentColor" size={13} className="text-textDefault-caption" />
            {word("global.return")}
          </Button>

          <Text fontStyle="BL" className="text-center">
            {word("program.subscribers.subscriptionDetails.discount.title")}
          </Text>
        </div>
      </div>

      {/* CONTENT */}
      {isResumeVisible ? (
        // RESUME
        <div className="flex grow flex-col gap-5 pb-2">
          <div className="flex flex-col gap-1">
            <Text fontStyle="LM" className="text-textDefault-heading">
              {word("program.subscribers.subscriptionDetails.discount.supensionTypeLabel")}
            </Text>
            {subscriberDetails.referencePrice && (
              <Text fontStyle="TM" className="mt-2 text-textDefault-body">
                {`${getFormattedCouponReduction(
                  formik.values.type,
                  formik.values.value,
                  language,
                  subscriberDetails.referencePrice.currency,
                )} ${word("program.subscribers.subscriptionDetails.discount.off")}`}
              </Text>
            )}
          </div>

          <div className="flex w-full justify-between px-4">
            <div className="flex flex-col items-center">
              <Text fontStyle="LS" className="text-textDefault-body">
                {word("program.subscribers.subscriptionDetails.discount.subscriptionPriceLabel")}
              </Text>
              <Text fontStyle="TM" className="mt-2 text-textError line-through decoration-current decoration-1">
                {formatCurrency(
                  subscriberDetails.paidPrice?.value ?? 0,
                  subscriberDetails.paidPrice?.currency,
                  language,
                )}
              </Text>
            </div>

            <div className="flex items-end">
              <ArrowDrawIcon color="currentColor" className="text-textDefault-body" />
            </div>

            <div className="flex flex-col items-center">
              <Text fontStyle="LS" className="mt-2 text-textDefault-body">
                {word("program.subscribers.subscriptionDetails.discount.subscriptionPriceAfterDiscountLabel")}
              </Text>
              <Text fontStyle="TM" className="mt-2 text-textSuccess">
                {formatCurrency(priceDiscount, subscriberDetails.referencePrice?.currency, language)}
              </Text>
            </div>
          </div>

          <div className="flex flex-col">
            <Text fontStyle="TM" className="text-textDefault-heading">
              {subscriberDetails.coupon?.duration !== null
                ? word("box.member.table.offer.resume.defineDuration", {
                    count: subscriberDetails.coupon?.duration,
                  })
                : word("box.member.table.offer.resume.unlimitedDuration")}
            </Text>
            <Text fontStyle="LM" className="mt-2 text-textDefault-body">
              {`${word("box.member.table.offer.resume.unlimitedDurationStart")} ${dayjs(subscriberDetails.coupon?.createdAt).format("LL")}`}
            </Text>
          </div>

          <div className="mt-auto flex w-full gap-2">
            <Button
              variant="outline"
              size="lg"
              onClick={() => handleOnDelete()}
              disabled={isSubmitting || isRemovingSubscriptionDiscount}
              isLoading={isRemovingSubscriptionDiscount}
              className="flex grow"
            >
              {isRemovingSubscriptionDiscount && <LoaderCircle className="animate-spin" />}
              {word("global.delete")}
            </Button>

            <Button
              variant="primary"
              size="lg"
              className="flex grow"
              onClick={() => setIsResumeVisible(false)}
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              {isSubmitting && <LoaderCircle className="animate-spin" />}
              {word("global.edit")}
            </Button>
          </div>
        </div>
      ) : (
        // FORMIK
        <div className="flex flex-col gap-2 pb-2">
          <DropdownInput
            label={word("program.subscribers.subscriptionDetails.discount.supensionTypeLabel")}
            options={discountTypesOptions}
            isRequiredInput={true}
            error={getFormErrorMessage("type", formik)}
          >
            <Select
              defaultValue={formik.values.type}
              onValueChange={(value) => {
                void formik.setFieldValue("type", value)
              }}
              items={discountTypesOptions}
            >
              <SelectTrigger id={"type"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {discountTypesOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DropdownInput>

          <div className="flex grow">
            <StandardInput
              isRequiredInput
              getFieldProps={formik.getFieldProps}
              name="value"
              error={getFormErrorMessage("value", formik)}
              setFieldValue={formik.setFieldValue}
              containerClassName="flex flex-row items-center"
              inputProps={{
                type: "number",
                inputMode: "numeric",
                max: formik.values.type === CouponType.PERCENT ? 100 : subscriberDetails.paidPrice?.value,
                step: formik.values.type === CouponType.PERCENT ? 1 : 0.01,
                min: 0,
                disabled: isSubmitting,
                onChange: (e) => {
                  if (formik.values.type === CouponType.PERCENT) {
                    void formik.setFieldValue("value", parseInt(e.target.value))
                  } else {
                    void formik.setFieldValue("value", parseFloat(e.target.value))
                  }
                },
              }}
            >
              {formik.values.type === CouponType.PERCENT ? (
                <Text fontStyle="TS" className="mr-3 ml-1 flex items-center justify-center text-textDefault-body">
                  %
                </Text>
              ) : (
                <Text fontStyle="TS" className="mr-3 ml-1 flex items-center justify-center text-textDefault-body">
                  {subscriberDetails.referencePrice?.currency
                    ? getCurrencySymbol(subscriberDetails.referencePrice.currency, language)
                    : ""}
                </Text>
              )}
            </StandardInput>
          </div>
          <div className="mt-2 mb-4 flex w-full justify-between px-4">
            <div className="flex flex-col items-center">
              <Text fontStyle="LS" className="mt-2 text-textDefault-body">
                {word("program.subscribers.subscriptionDetails.discount.subscriptionPriceLabel")}
              </Text>
              <Text
                fontStyle="TM"
                className={`mt-2 ${priceDiscount >= 0 ? "text-textError" : "text-textDefault-body"}`}
                style={priceDiscount >= 0 ? { textDecorationThickness: "1px", textDecorationLine: "line-through" } : {}}
              >
                {formatCurrency(
                  subscriberDetails.paidPrice?.value ?? 0,
                  subscriberDetails.paidPrice?.currency,
                  language,
                )}
              </Text>
            </div>
            {priceDiscount > 0 ? (
              <div className="flex items-end">
                <ArrowDrawIcon color="currentColor" className="text-textDefault-body" />
              </div>
            ) : null}
            <div className="flex flex-col items-center">
              <Text fontStyle="LS" className="mt-2 text-textDefault-body">
                {word("program.subscribers.subscriptionDetails.discount.subscriptionPriceAfterDiscountLabel")}
              </Text>
              <Text
                fontStyle="TM"
                className={`mt-2 ${priceDiscount >= 0 ? "text-textSuccess" : "text-textDefault-body"}`}
              >
                {priceDiscount >= 0
                  ? formatCurrency(priceDiscount, subscriberDetails.referencePrice?.currency, language)
                  : "--"}
              </Text>
            </div>
          </div>

          <DropdownInput
            label={word("program.subscribers.subscriptionDetails.discount.durationTypeLabel")}
            options={durationTypesOptions}
            isRequiredInput={true}
            error={getFormErrorMessage("durationType", formik)}
          >
            <Select
              defaultValue={formik.values.durationType}
              onValueChange={(value) => {
                void formik.setFieldValue("durationType", value)
                if (value === DurationType.UNLIMITED) {
                  void formik.setFieldValue("months", null)
                } else {
                  void formik.setFieldValue("months", 1)
                }
              }}
              items={durationTypesOptions}
            >
              <SelectTrigger id={"durationType"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {durationTypesOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DropdownInput>

          {formik.values.durationType === DurationType.UNLIMITED ? null : (
            <StandardInput
              isRequiredInput
              getFieldProps={formik.getFieldProps}
              name="months"
              error={getFormErrorMessage("months", formik)}
              setFieldValue={formik.setFieldValue}
              containerClassName="flex flex-row items-center"
              inputProps={{
                type: "number",
                inputMode: "numeric",
                step: 1,
                min: 0,
                onChange: (e) => {
                  void formik.setFieldValue("months", parseInt(e.target.value))
                },
              }}
            >
              <Text
                fontStyle="TS"
                className="mr-3 ml-1 flex items-center justify-center text-center text-textDefault-body"
              >
                {word("global.month.label", { count: formik.values.months ?? 1 })}
              </Text>
            </StandardInput>
          )}

          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={() => void formik.submitForm()}
            isLoading={isSubmitting}
            disabled={isSubmitting || !formik.isValid || !formik.dirty}
          >
            {isSubmitting && <LoaderCircle className="animate-spin" />}
            {word("global.save")}
          </Button>
        </div>
      )}
    </div>
  )
}
